import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import LoadingScreen from "../../../../shared/components/LoadingScreen";
import OrdersToolbar from "./OrdersToolbar";
import DeleteDialog from "./DeleteDialog";
import CommonTable, { HeadCell } from "../../../grocery/components/CommonTable";
import { useOrders } from "../../../../shared/hooks/useOrders";
import useImport from "./useImport";
import useExport from "./useExport";
import { Box, IconButton, TablePagination, Tooltip } from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import DeleteIcon from "@mui/icons-material/Delete";
import { OrderTableRow } from "../../../../shared/services/interface";
import { PageTitle } from "src/shared/PageTitle";
import { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "src/business-types/grocery/hooks/tableConfig";
import { useUser } from "src/business-types/grocery/components/UserContext";

export const formatNumber = (value: number) => {
  return value < 1000 ? value.toString().padStart(4, "0") : value.toString();
};

const OrderSearch: React.FC = () => {
  const navigation = useNavigate();
  const [, setBusinessType] = useState<string>("");
  const [isEmployee, setIsEmployee] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const { userConfigration } = useUser();
  const { inputRef, handleImport } = useImport({ setLoading });
  const { handleExport } = useExport({ setLoading });

  const {
    orders,
    loadings,
    page,
    rowsPerPage,
    paginationData,
    searchTerm,
    selectedOption,
    selectedDate,
    selectedDate2,
    handleSearch,
    handleFilterChange,
    applyCustomDateFilter,
    setSelectedDate,
    setSelectedDate2,
    deleteOrder,
    handleChangePage,
    handleChangeRowsPerPage,
    getDateRange,
  } = useOrders({});

  useEffect(() => {
    document.title = "Orders › iMetanic";
    setBusinessType(sessionStorage.getItem("business_type") || "");
    setIsEmployee(!!sessionStorage.getItem("employee"));
  }, []);

  const handleViewOrder = (orderId: string) => navigation(isEmployee ? `/v1/employee/order-details/${orderId}` : `/v1/order-details/${orderId}`);

  const handleOpenDialog = (orderId: number) => {
    setSelectedOrderId(orderId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedOrderId(null);
  };

  const handleConfirmDelete = () => {
    if (selectedOrderId !== null) deleteOrder(selectedOrderId);
    handleCloseDialog();
  };

  const toBoolean = (value: any) => value === true || value === "true";
  const billNoEnabled = toBoolean(userConfigration?.billNoEnabled);
  const orderIdEnabled = toBoolean(userConfigration?.orderIdEnabled);

  // Build head cells dynamically based on enabled features
  const orderHeadCells: HeadCell<OrderTableRow>[] = [
    { id: "S.No" as const, label: "S.No" },
    { id: "Name" as const, label: "Name" },
    { id: "Phone number" as const, label: "Phone number" },
  ];

  // Add Order ID column if enabled
  if (orderIdEnabled) {
    orderHeadCells.push({ id: "Order ID" as const, label: "Order ID" });
  }

  // Add Bill No column if enabled
  if (billNoEnabled) {
    orderHeadCells.push({ id: "BillNo" as const, label: "Bill No." });
  }

  // Add remaining columns
  orderHeadCells.push(
    { id: "Order status" as const, label: "Order status" },
    { id: "Actions" as const, label: "Actions" }
  );

  const transformedOrders: OrderTableRow[] = orders
    .filter((order) => order.OrderStatus !== "deleted")
    .map((order, index) => {
      const row: any = {
        id: order.Orderid,
        "S.No": index + 1 + page * rowsPerPage,
        Name: order.CustomerName,
        "Phone number": String(order.CustomerPhoneNo),
        "Order status": order.OrderStatus,
        Actions: (
          <Box display='flex' alignItems='center' gap={1}>
            <Tooltip title='View order'>
              <IconButton onClick={() => handleViewOrder(String(order.Orderid))}>
                <ViewListIcon sx={{ color: "#000" }} />
              </IconButton>
            </Tooltip>
            {order.OrderStatus === "Cancelled" && (
              <Tooltip title='Delete order'>
                <IconButton onClick={() => handleOpenDialog(order.Orderid)}>
                  <DeleteIcon sx={{ color: "#d32f2f" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      };

      // Add Order ID if enabled
      if (orderIdEnabled) {
        row["Order ID"] = order.Orderid;
      }

      // Add Bill No if enabled
      if (billNoEnabled) {
        row["BillNo"] = order.billNumber ? formatNumber(order.billNumber) : "N/A";
      }

      return row;
    });

  return (
    <div className='flex flex-col h-[90vh] overflow-hidden'>
      <PageTitle title='- Orders' />
      <div className='main-order-wrapper'>
        <OrdersToolbar
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          inputRef={inputRef}
          handleImport={handleImport}
          handleExport={(type) =>
            handleExport(type, orders, selectedOption, searchTerm, page, rowsPerPage, getDateRange, selectedDate, selectedDate2)
          }
          orders={orders}
          selectedDate={selectedDate}
          selectedDate2={selectedDate2}
          selectedOption={selectedOption}
          handleFilterChange={handleFilterChange}
        />

        {selectedOption === "CustomizeDate" && (
          <p className='Select-Date-cus'>
            From: <input type='date' value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} /> To:{" "}
            <input type='date' value={selectedDate2} onChange={(e) => setSelectedDate2(e.target.value)} />{" "}
            <button onClick={applyCustomDateFilter}>🔍</button>
          </p>
        )}
        <div className='w-full overflow-y-auto'>
          <CommonTable<OrderTableRow>
            data={transformedOrders}
            headCells={orderHeadCells}
            title=''
            showToolbar={false}
            selectable={false}
            denseToggle={false}
            pageSize={rowsPerPage}
            pagenation={false}
            showFilterIcon={false}
          />
          <div className='flex justify-end mr-8 mt-2'>
            <TablePagination
              component='div'
              count={paginationData.totalRecords}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            />
          </div>
        </div>

        <DeleteDialog open={openDialog} onClose={handleCloseDialog} onConfirm={handleConfirmDelete} />
      </div>

      {(loading || loadings) && <LoadingScreen />}
    </div>
  );
};

export default OrderSearch;